import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotifications, markNotificationRead, snoozeNotification, useShelterNotificationSettings, CardSkeleton } from '@rehome/shared';
import { Bell, AlertOctagon, PackageX, Stethoscope, ScanFace, Check, Clock, ChevronDown, ChevronUp } from 'lucide-react';

// ─── NotificationsCenterPage ───────────────────────────────────────────────
// Scoped to the shelter in the URL (/shelter/:shelterId/notifications).
// ===========================================================================

const NotificationsCenterPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const [filter, setFilter] = useState<'All' | 'Critical' | 'High'>('All');
  const { data: rawNotifications, loading, refetch } = useNotifications(shelterId);
  const { data: notificationSettings } = useShelterNotificationSettings(shelterId);
  const [marking, setMarking] = useState<string | null>(null);
  const [snoozing, setSnoozing] = useState<string | null>(null);
  const [showSnoozed, setShowSnoozed] = useState(false);

  const snoozeMinutes = notificationSettings?.snoozeDurationMinutes ?? 60;

  const handleMarkRead = async (id: string) => {
    setMarking(id);
    const ok = await markNotificationRead(id);
    setMarking(null);
    if (ok) refetch();
  };

  const handleSnooze = async (id: string) => {
    setSnoozing(id);
    const ok = await snoozeNotification(id, snoozeMinutes);
    setSnoozing(null);
    if (ok) refetch();
  };

  const notifications = useMemo(() => {
    return [...(rawNotifications || [])].sort((a, b) => {
      const score: Record<string, number> = { Critical: 3, High: 2, Medium: 1, Low: 0 };
      if (score[b.severity] !== score[a.severity]) return score[b.severity] - score[a.severity];
      const aTime = a.timestamp || a.createdAt || '';
      const bTime = b.timestamp || b.createdAt || '';
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  }, [rawNotifications]);

  const now = Date.now();
  const isSnoozed = (n: (typeof notifications)[number]) => !!n.snoozedUntil && new Date(n.snoozedUntil).getTime() > now;

  const filteredAll = notifications.filter(n => filter === 'All' || n.severity === filter);
  const filtered = filteredAll.filter(n => !isSnoozed(n));
  const snoozedCount = filteredAll.filter(isSnoozed).length;

  const getIcon = (cat: string, sev: string) => {
    if (sev === 'Critical') return <AlertOctagon className="w-6 h-6 text-red-600" />;
    if (cat === 'Inventory') return <PackageX className="w-6 h-6 text-orange-600" />;
    if (cat === 'Medical') return <Stethoscope className="w-6 h-6 text-blue-600" />;
    if (cat === 'Security') return <ScanFace className="w-6 h-6 text-red-600" />;
    return <Bell className="w-6 h-6 text-gray-600" />;
  };

  const severityStyle = (s: string) => {
    switch (s) {
      case 'Critical': return 'border-l-4 border-l-red-600 bg-red-50';
      case 'High': return 'border-l-4 border-l-orange-500 bg-orange-50';
      case 'Medium': return 'border-l-4 border-l-blue-500 bg-blue-50';
      default: return 'border-l-4 border-l-gray-300 bg-gray-50';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <div className="flex justify-between items-center">
        <div><h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3"><Bell className="w-8 h-8 text-teal-600" />Értesítési Központ</h1><p className="text-gray-600 mt-1">Automatikus rendszerfigyelmeztetések és teendők</p></div>
        <div className="bg-white px-4 py-2 rounded-lg shadow-sm border font-medium text-gray-700">Összesen: <span className="text-teal-700 font-bold">{notifications.length}</span> db</div>
      </div>

      {loading && (
        <div className="space-y-3">
          <CardSkeleton /><CardSkeleton /><CardSkeleton />
        </div>
      )}

      {!loading && (<>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {(['All', 'Critical', 'High'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${filter === f ? (f === 'Critical' ? 'bg-red-600 text-white border-red-600' : f === 'High' ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-800 text-white border-gray-800') : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {f === 'All' ? 'Összes' : f === 'Critical' ? 'Kritikus' : 'Magas prioritás'}
            </button>
          ))}
        </div>
        {snoozedCount > 0 && (
          <button onClick={() => setShowSnoozed(prev => !prev)} className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-teal-700 px-2 py-1">
            <Clock className="w-3.5 h-3.5" /> {snoozedCount} elhalasztva
            {showSnoozed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {(showSnoozed ? filteredAll : filtered).map(n => {
          const snoozed = isSnoozed(n);
          return (
          <div key={n.id} className={`${severityStyle(n.severity)} bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all flex items-start gap-4 ${n.isRead || snoozed ? 'opacity-60' : ''}`}>
            <div className="p-2 bg-white rounded-full shadow-sm">{getIcon(n.category, n.severity)}</div>
            <div className="flex-1">
              <div className="flex justify-between items-start"><h3 className="font-bold text-gray-900">{n.title}</h3><span className={`text-xs font-bold px-2 py-1 rounded-full ${n.severity === 'Critical' ? 'bg-red-100 text-red-800' : n.severity === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>{n.severity}</span></div>
              <p className="text-sm text-gray-600 mt-1">{n.message}</p>
              <div className="flex justify-between items-center mt-3">
                <span className="text-xs text-gray-400">
                  {new Date(n.timestamp || n.createdAt || '').toLocaleString('hu-HU')}
                  {snoozed && n.snoozedUntil && ` · Elhalasztva eddig: ${new Date(n.snoozedUntil).toLocaleString('hu-HU')}`}
                </span>
                <div className="flex items-center gap-2">
                  {!n.isRead && (
                    <>
                      <button onClick={() => handleSnooze(n.id)} disabled={snoozing === n.id}
                        className="text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 border border-gray-200 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                        <Clock className="w-3 h-3" /> Később
                      </button>
                      <button onClick={() => handleMarkRead(n.id)} disabled={marking === n.id}
                        className="text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                        <Check className="w-3 h-3" /> OK
                      </button>
                    </>
                  )}
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">{n.category}</span>
                </div>
              </div>
            </div>
          </div>
        );})}
      </div>
      </>)}
    </div>
  );
};

export default NotificationsCenterPage;
